import { Switch, SwitchProps, styled } from '@mui/material';
import { useCallback } from 'react';

export type ToggleProps = {
  active?: boolean;
  onChange: (active: boolean) => void;
  size?: 'small' | 'large';
} & Omit<SwitchProps, 'checked' | 'onChange' | 'size'>;

const StyledSwitch = styled(Switch)<{ toggleSize?: 'small' | 'large' }>(
  ({ theme, toggleSize }) => ({
    width: toggleSize === 'small' ? 24 : 36,
    height: toggleSize === 'small' ? 12 : 20,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: toggleSize === 'small' ? 0.5 : 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: `translateX(${toggleSize === 'small' ? 12 : 16}px)`,
        color: theme.palette.common.white,
        '& + .MuiSwitch-track': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.common.white
              : theme.palette.common.black,
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: toggleSize === 'small' ? 8 : 16,
      height: toggleSize === 'small' ? 8 : 16,
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.common.black
          : theme.palette.common.white,
    },
    '& .MuiSwitch-track': {
      borderRadius: toggleSize === 'small' ? 6 : 10,
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(0, 0, 0, 0.2)',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  })
);

export const Toggle = ({
  onChange,
  active = false,
  size = 'large',
  ...rest
}: ToggleProps) => {
  const handleChange = useCallback(() => {
    onChange(!active);
  }, [active, onChange]);

  return (
    <StyledSwitch
      checked={active}
      onChange={handleChange}
      toggleSize={size}
      {...rest}
    />
  );
};
